import cn from '@sindresorhus/class-names';
import Image from 'next/image';
import Link from 'next/link';
import {memo, useCallback} from 'react';

import {Text} from '@web-catalog/ui-kit/Text';

import {stringFormat} from '@web-catalog/utils';

import {Icon} from '../Icon';
import {AppsSvg, GamesSvg} from '../SvgIcons';
import styles from './CategoryString.module.scss';

type Props = {
  className?: string;
  appsRoute: string;
  icon: string;
  categoryName: string;
  category: string;
  activeCategory: string | Array<string> | undefined;
  onClickCategory: (app_category_name: string) => void;
  linkTarget?: string;
};

type CategoryIconProps = {
  icon: string;
  categoryName?: string;
};

export const CategoryString = memo<Props>(
  ({
    className,
    appsRoute,
    icon,
    categoryName,
    category,
    activeCategory,
    onClickCategory,
    linkTarget,
  }) => {
    const handleOnClickCategory = useCallback(() => {
      onClickCategory(category);
    }, [category, onClickCategory]);

    return (
      <Link
        data-testid="CategoryStringLink"
        href={appsRoute}
        scroll={false}
        target={linkTarget}
        className={cn(className, styles.category, {
          [styles.categoryActive]: category === activeCategory,
        })}
        onClick={handleOnClickCategory}
        aria-label={`${categoryName}`}
        prefetch={false}
      >
        <div className={styles.categoryIcon}>
          <CategoryIcon icon={icon} />
        </div>

        <Text
          className={styles.categoryName}
          typography={'content-s'}
          color={category === activeCategory ? 'system-white-100' : 'black-100'}
        >
          {stringFormat(categoryName)}
        </Text>
      </Link>
    );
  },
);

CategoryString.displayName = 'CategoryString';

const IconsConfig: Record<string, unknown> = {
  apps: AppsSvg,
  games: GamesSvg,
};

const CategoryIcon = memo<CategoryIconProps>(({icon}) => {
  if (IconsConfig[icon]) {
    return <Icon svg={IconsConfig[icon]} />;
  }

  return <Image src={icon} alt="" width={20} height={20} />;
});

CategoryIcon.displayName = 'CategoryIcon';
